import { Component, CUSTOM_ELEMENTS_SCHEMA, Input } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from '../../shared-components/loader/loader.component';
import { ManualApprovalHeader } from '../../models/manual-approval-header.model';
import { ToastrService } from '../../services/toastr-service';
import { debounceTime, distinctUntilChanged, ReplaySubject, takeUntil } from 'rxjs';
import { ApprovalFilterForm } from '../../form-model/work-order-filter.form.model';
import { WorkOrderSearchTextForm } from '../../form-model/work-order-search-text.form.model';
import { myPoolFilter } from '../../models/manual-approval-filter.model';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import { MaintenanceOrderHeader } from '../../models/maintenance-order-header.model';
import { utils } from '../../helper/utils';
import { APICallStatus } from '../../models/api-call-status.mode';
import { ApprovalWoService } from '../../services/approval-wo.service';

@Component({
  selector: 'app-manual-approval-ttl-my-wo',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, LoaderComponent, NgxSkeletonLoaderModule],
  templateUrl: './manual-approval-ttl-my-wo.component.html',
  styleUrl: './manual-approval-ttl-my-wo.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ManualApprovalTtlMyWoComponent {
  selected = [''];
  data: ManualApprovalHeader[] = [];
  mockDataForLoadingAnimation: { id: number }[] = [...Array(30).keys()].map((i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 20;
  sort: string | undefined;
  urlNavigate: string | undefined;
  filterParam: myPoolFilter = {};
  getWorkOrdersApiCallStatus: APICallStatus = 'NOT_STARTED';
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    workOrderFilterForm: FormGroup<ApprovalFilterForm>;
    workOrderSearchTextForm: FormGroup<WorkOrderSearchTextForm>;
  }>;
  columns: Array<TableColumn> = [
    {
      id: 'isPriority',
      label: '',
      sortDisabled: true,
    },
    {
      id: 'maintenanceOrderNumber',
      label: 'Work Order Number',
      sortDisabled: true,
    },
    {
      id: 'equipmentNumber',
      label: 'Equipment Number',
      sortDisabled: true,
    },
    {
      id: 'totalOrderCostUSD',
      label: 'Total Value(USD)',
      dataType: {
        type: 'number',
        formatter: (value, row) => {
          return utils.numberFormatter.format(Number(value));
        },
      },
    },
    {
      id: 'totalOrderCost',
      label: 'Total Value(Shop Currency)',
      sortDisabled: true,
      dataType: {
        type: 'number',
        formatter: (value, row) => {
          return `${(row.original as MaintenanceOrderHeader).isoCurrencyCode} ${utils.numberFormatter.format(Number(value))}`;
        },
      },
    },
    {
      id: 'maintenanceRepairModeCode',
      label: 'Mode',
    },
    {
      id: 'maintenanceOrderStatusName',
      label: 'Status',
      sortDisabled: true,
    },
    {
      id: 'maintenanceOrderStatusDuration',
      label: 'Status Duration',
      dataType: {
        type: 'string',
        formatter: (cellValue: unknown) => utils.formatDuration(cellValue as number),
      },
    },
    {
      id: 'createTimestamp',
      label: 'Created On',
      sortDisabled: true,
      dataType: {
        type: 'string',
        formatter: (value) => {
          const date = new Date(value as string);
          return `${utils.dateFormatter.format(date)}`;
        },
      },
    },
    {
      id: 'createUserID',
      label: 'Created By',
      sortDisabled: true,
    },
    {
      id: 'maintenanceShopCode',
      label: 'Shop ID',
    },
    {
      id: 'locationCode',
      label: 'Location',
      sortDisabled: true,
    },
  ];
  constructor(
    private toastrService: ToastrService,
    private approvalService: ApprovalWoService,
  ) {}

  ngOnInit() {
    this.sort = `createTimestamp:desc,updateTimestamp:desc`;
    this.urlNavigate = '/approvals/my-workorders';
    this.getMyQueueTTLApprover();
    this.subscribeToFormChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  private subscribeToFormChanges() {
    this.filterFormGroup.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      if (this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter.value) {
        this.onApplyFilter();
        this.getMyQueueTTLApprover();
      }
    });
  }
  onApplyFilter() {
    if (this.filterFormGroup.controls.workOrderFilterForm.valid) {
      const filters: myPoolFilter = {};

      if (this.filterFormGroup.controls.workOrderSearchTextForm.controls.searchText.value) {
        filters.searchText = utils.normalizeSearchTextForApi(this.filterFormGroup.controls.workOrderSearchTextForm.controls.searchText.value);
      }
      this.filterParam = filters;
      this.currentPage = 1;
    }
  }

  onSortDirectionChange(event: Event) {
    let customEvent = event as CustomEvent;
    let sortColumn: string = this.sortColumnName(customEvent.detail.column);
    let sortDirection: string = customEvent.detail.direction == 'ascending' ? 'asc' : 'desc';
    if (sortColumn != 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getMyQueueTTLApprover();
    }
  }
  private sortColumnName(gridColumnName: string): string {
    switch (gridColumnName) {
      case 'maintenanceOrderStatusDuration':
        return 'maintenanceOrderStatusUpdatedTimestamp';
      case 'totalOrderCostUSD':
        return 'totalOrderCost';
      default:
        return gridColumnName;
    }
  }

  onPageLimitChange(event: Event) {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;

    this.getMyQueueTTLApprover();
  }

  onPageChange(event: Event) {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getMyQueueTTLApprover();
    }
  }
  getMyQueueTTLApprover() {
    this.getWorkOrdersApiCallStatus = 'IN_PROGRESS';
    this.filterParam.page = this.filterParam.page ? this.filterParam.page : '';
    this.filterParam.limit = this.filterParam.limit ? this.filterParam.limit : '';
    this.filterParam.sort = this.filterParam.sort ? this.filterParam.sort : '';
    this.filterParam.searchText = this.filterParam.searchText ? this.filterParam.searchText : '';
    this.approvalService
      .getMyQueueWorkOrder(this.currentPage, this.pageLimit, this.sort, this.filterParam)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const headers = response.headers;
          const body = response.body || [];
          this.data = body;
          this.firstPage = headers.get('First-Page') ? +headers.get('First-Page')! : undefined;
          this.previousPage = headers.get('Previous-Page') ? +headers.get('Previous-Page')! : undefined;
          this.currentPage = +headers.get('Current-Page')!;
          this.nextPage = headers.get('Next-Page') ? +headers.get('Next-Page')! : undefined;
          this.lastPage = headers.get('Last-Page') ? +headers.get('Last-Page')! : undefined;
          this.totalItemCount = headers.get('Total-Item-Count') ? +headers.get('Total-Item-Count')! : undefined;
          this.apiVersion = headers.get('API-Version') ?? undefined;
          this.getWorkOrdersApiCallStatus = 'COMPLETED_SUCCESFULLY';
        },
        error: (err) => {
          this.getWorkOrdersApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }
}
